// ============================================================================
//  SUPPORT CHAT MODAL - Enhanced with Ticket List & Real-time Chat

// ============================================================================
// Full support experience for users:
// - View all tickets (open/resolved)
// - Create new tickets
// - Real-time chat with admin
// - Email preference toggle
// - Move to email option
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  Textarea,
  Input,
  Switch,
  useToast,
  Spinner,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import supportService, {
  SupportTicket,
  TicketMessage,
  TicketCategory,
  TICKET_CATEGORIES,
  TICKET_STATUSES,
} from '../../services/supportService';
import '../../styles/SupportChatModal.css';

interface SupportChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ViewState = 'list' | 'new' | 'chat';

const SupportChatModal: React.FC<SupportChatModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { currentUser, userProfile } = useAuth();
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // View state
  const [view, setView] = useState<ViewState>('list');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Ticket list
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(true);

  // New ticket form
  const [selectedCategory, setSelectedCategory] = useState<TicketCategory | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [emailUpdates, setEmailUpdates] = useState(true);

  // Chat view
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [chatMessage, setChatMessage] = useState('');

  // ============================================================================
  // LOAD TICKETS
  // ============================================================================

  useEffect(() => {
    if (!isOpen || !currentUser) return;

    setLoadingTickets(true);
    
    const unsubscribe = supportService.subscribeToUserTickets(
      currentUser.uid,
      (updatedTickets) => {
        setTickets(updatedTickets);
        setLoadingTickets(false);
        
        // Update active ticket if viewing
        if (activeTicket) {
          const updated = updatedTickets.find(t => t.id === activeTicket.id);
          if (updated) setActiveTicket(updated);
        }
      }
    );

    return () => unsubscribe();
  }, [isOpen, currentUser]);

  // ============================================================================
  // LOAD MESSAGES FOR ACTIVE TICKET
  // ============================================================================

  useEffect(() => {
    if (!activeTicket) return;

    const unsubscribe = supportService.subscribeToMessages(
      activeTicket.id,
      (updatedMessages) => {
        setMessages(updatedMessages);
        // Scroll to bottom
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    );

    // Mark as read
    supportService.markMessagesAsRead(activeTicket.id, false);

    return () => unsubscribe();
  }, [activeTicket?.id]);

  // ============================================================================
  // CREATE NEW TICKET
  // ============================================================================

  const handleCreateTicket = async () => {
    if (!currentUser || !selectedCategory || !newMessage.trim()) {
      toast({
        title: 'Missing information',
        description: 'Please select a category and enter your message',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const ticketId = await supportService.createTicket({
        odid: currentUser.uid,
        username: userProfile?.username || 'User',
        email: userProfile?.email || currentUser.email || '',
        category: selectedCategory,
        message: newMessage.trim(),
        emailUpdates,
      });

      toast({
        title: 'Ticket created',
        description: 'Our support team will respond soon',
        status: 'success',
        duration: 3000,
      });

      // Reset and go to ticket list
      setSelectedCategory(null);
      setNewMessage('');
      setView('list');

      // Open the new ticket chat
      const ticket = await supportService.getTicket(ticketId);
      if (ticket) {
        setActiveTicket(ticket);
        setView('chat');
      }
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      toast({
        title: 'Error',
        description: 'Could not create ticket. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // SEND MESSAGE
  // ============================================================================

  const handleSendMessage = async () => {
    if (!activeTicket || !currentUser || !chatMessage.trim()) return;

    setSending(true);

    try {
      await supportService.sendMessage(
        activeTicket.id,
        currentUser.uid,
        userProfile?.username || 'User',
        chatMessage.trim(),
        false // isAdmin
      );

      setChatMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Could not send message',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setSending(false);
    }
  };

  // ============================================================================
  // MOVE TO EMAIL
  // ============================================================================

  const handleMoveToEmail = async () => {
    if (!activeTicket) return;

    try {
      await supportService.moveToEmail(activeTicket.id);
      toast({
        title: 'Moved to email',
        description: 'Further responses will be sent to your email',
        status: 'info',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error moving to email:', error);
    }
  };

  // ============================================================================
  // TOGGLE EMAIL UPDATES
  // ============================================================================

  const handleToggleEmailUpdates = async (value: boolean) => {
    if (!activeTicket) return;

    try {
      await supportService.updateEmailPreference(activeTicket.id, value);
      setActiveTicket({ ...activeTicket, emailUpdates: value });
    } catch (error) {
      console.error('Error updating email preference:', error);
    }
  };

  // ============================================================================
  // CLOSE MODAL
  // ============================================================================

  const handleClose = () => {
    setView('list');
    setActiveTicket(null);
    setSelectedCategory(null);
    setNewMessage('');
    setChatMessage('');
    onClose();
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const getStatusBadge = (status: string) => {
    const statusInfo = TICKET_STATUSES.find(s => s.id === status);
    return (
      <Badge
        colorScheme={statusInfo?.color || 'gray'}
        fontSize="10px"
        px={2}
        py={0.5}
        borderRadius="full"
      >
        {statusInfo?.label || status}
      </Badge>
    );
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const formatMessageTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // ============================================================================
  // RENDER: TICKET LIST
  // ============================================================================

  const renderTicketList = () => (
    <>
      {/* Header */}
      <HStack 
        justify="space-between" 
        px={4} 
        py={4}
        pt="calc(env(safe-area-inset-top, 0px) + 16px)"
        borderBottom="1px solid"
        borderColor="gray.800"
        minH="60px"
        position="sticky"
        top={0}
        bg="#0D0D0D"
        zIndex={10}
      >
        <Text
          color="gray.400"
          fontSize="md"
          cursor="pointer"
          onClick={handleClose}
          _hover={{ color: 'white' }}
          minW="60px"
        >
          Close
        </Text>
        <Text color="white" fontSize="md" fontWeight="semibold">
          Support
        </Text>
        <Text
          color="cyan.400"
          fontSize="md"
          fontWeight="semibold"
          cursor="pointer"
          onClick={() => setView('new')}
          _hover={{ color: 'cyan.300' }}
          minW="60px"
          textAlign="right"
        >
          + New
        </Text>
      </HStack>

      <ModalBody py={4} px={4} overflowY="auto" className="support-modal-body">
        {loadingTickets ? (
          <VStack py={10}>
            <Spinner color="cyan.400" size="lg" />
            <Text color="gray.500">Loading tickets...</Text>
          </VStack>
        ) : tickets.length === 0 ? (
          <VStack py={10} spacing={4}>
            <Text fontSize="48px"></Text>

            <Text color="gray.400" textAlign="center">
              No support tickets yet
            </Text>
            <Text color="gray.600" fontSize="sm" textAlign="center">
              Tap "+ New" to create a support request
            </Text>
          </VStack>
        ) : (
          <VStack spacing={2} align="stretch">
            {tickets.map(ticket => (
              <Box
                key={ticket.id}
                className="support-ticket-item"
                onClick={async () => {
                  setActiveTicket(ticket);
                  setView('chat');
                  //  FIX: Immediately mark as read when clicking ticket

                  if (ticket.unreadByUser > 0) {
                    try {
                      await supportService.markMessagesAsRead(ticket.id, false);
                    } catch (err) {
                      console.error('Failed to mark as read:', err);
                    }
                  }
                }}
              >
                <HStack justify="space-between" mb={1}>
                  <HStack spacing={2}>
                    <Text fontSize="lg">
                      {TICKET_CATEGORIES.find(c => c.id === ticket.category)?.icon || ''}

                    </Text>
                    <Text color="white" fontSize="sm" fontWeight="medium" noOfLines={1}>
                      {ticket.subject}
                    </Text>
                  </HStack>
                  {ticket.unreadByUser > 0 && (
                    <Badge colorScheme="red" borderRadius="full" px={2}>
                      {ticket.unreadByUser}
                    </Badge>
                  )}
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500" fontSize="xs" noOfLines={1} flex={1}>
                    {ticket.lastMessagePreview || 'No messages'}
                  </Text>
                  <HStack spacing={2}>
                    {getStatusBadge(ticket.status)}
                    <Text color="gray.600" fontSize="xs">
                      {formatTime(ticket.updatedAt)}
                    </Text>
                  </HStack>
                </HStack>
              </Box>
            ))}
          </VStack>
        )}

        {/* FAQ Section */}
        <Box mt={6}>
          <Text color="gray.500" fontSize="xs" textTransform="uppercase" mb={3}>
            Quick Answers
          </Text>
          <VStack spacing={2} align="stretch">
            <Box className="support-faq-item">
              <Text color="gray.300" fontSize="sm" fontWeight="medium">
                How do I redeem PSS Coins?
              </Text>
              <Text color="gray.500" fontSize="xs" mt={1}>
                Go to Wallet → Redeem → Enter amount → Submit bank details
              </Text>
            </Box>
            <Box className="support-faq-item">
              <Text color="gray.300" fontSize="sm" fontWeight="medium">
                What is KYC verification?
              </Text>
              <Text color="gray.500" fontSize="xs" mt={1}>
                Identity verification required for PSS Coins redemptions over $50
              </Text>
            </Box>
            <Box className="support-faq-item">
              <Text color="gray.300" fontSize="sm" fontWeight="medium">
                How do referral bonuses work?
              </Text>
              <Text color="gray.500" fontSize="xs" mt={1}>
                Earn 10% of the rake from players who sign up with your code
              </Text>
            </Box>
          </VStack>
        </Box>

        {/* Contact Info */}
        <Box className="support-contact-box" mt={6}>
          <Text color="gray.500" fontSize="sm">
            Need immediate help? Email us at
          </Text>
          <Text color="cyan.400" fontSize="sm" fontWeight="semibold">
            support@pokersweepstars.com
          </Text>
        </Box>

        <Box h="100px" />
      </ModalBody>
    </>
  );

  // ============================================================================
  // RENDER: NEW TICKET
  // ============================================================================

  const renderNewTicket = () => (
    <>
      {/* Header - FIXED LAYOUT */}
      <HStack 
        justify="space-between" 
        px={4} 
        py={4}
        pt="calc(env(safe-area-inset-top, 0px) + 16px)"
        borderBottom="1px solid"
        borderColor="gray.800"
        minH="60px"
        position="sticky"
        top={0}
        bg="#0D0D0D"
        zIndex={10}
      >
        <Text
          color="gray.400"
          fontSize="md"
          cursor="pointer"
          onClick={() => setView('list')}
          _hover={{ color: 'white' }}
          minW="60px"
        >
          ← Back
        </Text>
        <Text color="white" fontSize="md" fontWeight="semibold">
          New Ticket
        </Text>
        <Box
          as="button"
          color={selectedCategory && newMessage.trim() ? 'cyan.400' : 'gray.600'}
          fontSize="md"
          fontWeight="semibold"
          cursor={loading || !selectedCategory || !newMessage.trim() ? 'not-allowed' : 'pointer'}
          onClick={loading || !selectedCategory || !newMessage.trim() ? undefined : handleCreateTicket}
          opacity={loading ? 0.5 : 1}
          _hover={selectedCategory && newMessage.trim() ? { color: 'cyan.300' } : {}}
          minW="60px"
          textAlign="right"
          bg="transparent"
          border="none"
          disabled={loading || !selectedCategory || !newMessage.trim()}
        >
          {loading ? <Spinner size="sm" /> : 'Send'}
        </Box>
      </HStack>

      <ModalBody py={6} px={4} overflowY="auto" className="support-modal-body">
        <VStack spacing={6} align="stretch" maxW="500px" mx="auto">
          {/* Welcome */}
          <Box className="support-welcome-box">
            <Text color="cyan.200" fontSize="sm">
               Hi {userProfile?.username || 'there'}! How can we help you today?

            </Text>
          </Box>

          {/* Category Selection */}
          <Box>
            <Text color="gray.400" fontSize="xs" textTransform="uppercase" mb={3}>
              Select a category
            </Text>
            <VStack spacing={2} align="stretch">
              {TICKET_CATEGORIES.map(category => (
                <HStack
                  key={category.id}
                  className={`support-category-item ${selectedCategory === category.id ? 'selected' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <Text fontSize="lg">{category.icon}</Text>
                  <Text color="white" fontSize="sm" fontWeight="medium">
                    {category.label}
                  </Text>
                  {selectedCategory === category.id && (
                    <Text color="cyan.400" ml="auto"></Text>

                  )}
                </HStack>
              ))}
            </VStack>
          </Box>

          {/* Message Input */}
          {selectedCategory && (
            <Box>
              <Text color="gray.400" fontSize="xs" textTransform="uppercase" mb={3}>
                Describe your issue
              </Text>
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Please provide as much detail as possible..."
                className="support-textarea"
                rows={6}
                maxLength={1000}
              />
              <Text color="gray.600" fontSize="xs" textAlign="right" mt={1}>
                {newMessage.length}/1000
              </Text>
            </Box>
          )}

          {/* Email Updates Toggle */}
          {selectedCategory && (
            <HStack 
              justify="space-between" 
              className="support-email-toggle"
            >
              <VStack align="start" spacing={0}>
                <Text color="white" fontSize="sm">Email updates</Text>
                <Text color="gray.500" fontSize="xs">
                  Get notified when we respond
                </Text>
              </VStack>
              <Switch
                isChecked={emailUpdates}
                onChange={(e) => setEmailUpdates(e.target.checked)}
                colorScheme="cyan"
              />
            </HStack>
          )}

          <Box h="100px" />
        </VStack>
      </ModalBody>
    </>
  );

  // ============================================================================
  // RENDER: CHAT VIEW
  // ============================================================================

  const renderChat = () => (
    <>
      {/* Header */}
      <HStack 
        justify="space-between" 
        px={4} 
        py={4}
        pt="calc(env(safe-area-inset-top, 0px) + 16px)"
        borderBottom="1px solid"
        borderColor="gray.800"
        minH="60px"
        position="sticky"
        top={0}
        bg="#0D0D0D"
        zIndex={10}
      >
        <Text
          color="gray.400"
          fontSize="md"
          cursor="pointer"
          onClick={() => {
            setActiveTicket(null);
            setView('list');
          }}
          _hover={{ color: 'white' }}
          minW="60px"
        >
          ← Back
        </Text>
        <VStack spacing={0}>
          <Text color="white" fontSize="sm" fontWeight="semibold" noOfLines={1}>
            {TICKET_CATEGORIES.find(c => c.id === activeTicket?.category)?.label || 'Support'}
          </Text>
          {getStatusBadge(activeTicket?.status || 'open')}
        </VStack>
        <Text
          color="gray.400"
          fontSize="sm"
          cursor="pointer"
          onClick={() => {/* Options menu */}}
          minW="60px"
          textAlign="right"
        >
          •••
        </Text>
      </HStack>

      {/* Moved to Email Banner */}
      {activeTicket?.movedToEmail && (
        <Box className="support-email-banner">
          <Text> This conversation has been moved to email</Text>

        </Box>
      )}

      {/* Messages */}
      <Box className="support-messages-container">
        <VStack spacing={3} align="stretch" p={4}>
          {messages.map(msg => (
            <Box
              key={msg.id}
              className={`support-message ${msg.isAdmin ? 'admin' : 'user'} ${msg.isSystem ? 'system' : ''}`}
            >
              {msg.isSystem ? (
                <Text color="gray.500" fontSize="xs" textAlign="center">
                  {msg.message}
                </Text>
              ) : (
                <>
                  <Text color={msg.isAdmin ? 'gray.200' : 'white'} fontSize="sm">
                    {msg.message}
                  </Text>
                  <Text 
                    color={msg.isAdmin ? 'gray.500' : 'cyan.600'} 
                    fontSize="10px" 
                    mt={1}
                  >
                    {msg.isAdmin ? `Support • ${formatMessageTime(msg.createdAt)}` : formatMessageTime(msg.createdAt)}
                  </Text>
                </>
              )}
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Chat Input */}
      {!activeTicket?.movedToEmail && activeTicket?.status !== 'closed' && (
        <Box className="support-input-container">
          <HStack spacing={2}>
            <Textarea
              ref={inputRef}
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              placeholder="Type your message..."
              className="support-chat-input"
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Box
              className={`support-send-btn ${chatMessage.trim() ? 'active' : ''}`}
              onClick={handleSendMessage}
            >
              {sending ? <Spinner size="sm" /> : '↑'}
            </Box>
          </HStack>

          {/* Options */}
          <HStack justify="space-between" mt={2} px={1}>
            <HStack spacing={1}>
              <Switch
                size="sm"
                isChecked={activeTicket?.emailUpdates}
                onChange={(e) => handleToggleEmailUpdates(e.target.checked)}
                colorScheme="cyan"
              />
              <Text color="gray.500" fontSize="xs">Email updates</Text>
            </HStack>
            <Text
              color="gray.500"
              fontSize="xs"
              cursor="pointer"
              onClick={handleMoveToEmail}
              _hover={{ color: 'cyan.400' }}
            >
               Continue via email

            </Text>
          </HStack>
        </Box>
      )}

      {/* Closed Ticket Message */}
      {activeTicket?.status === 'closed' && (
        <Box className="support-closed-banner">
          <Text>This ticket has been closed</Text>
          <Text
            color="cyan.400"
            fontSize="sm"
            cursor="pointer"
            onClick={() => setView('new')}
            mt={2}
          >
            Open a new ticket
          </Text>
        </Box>
      )}
    </>
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="#0D0D0D" m={0} borderRadius={0} className="support-modal">
        {view === 'list' && renderTicketList()}
        {view === 'new' && renderNewTicket()}
        {view === 'chat' && renderChat()}
      </ModalContent>
    </Modal>
  );
};

export default SupportChatModal;